/* eslint-disable max-len */

import { useTranslation } from 'react-i18next';
import {
  capitalizeFirstLetterEveryword
} from '../../../shared/stringFunctions';
import { TXT_ADD_STRUCTURE, TXT_STRUCTURES } from '../../../translations/translationConstants';
import {
  TXT_SEARCH_BY_NAME
} from '../../../translations/translationConstants';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import {
  addStructureInput,
  deleteStructureInput,
  getLithologiesInput,
  getStructureOutput,
  GET_STRUCTURE,
  updateStructureInput
} from '../../../api';
import { useEffect } from 'react';
import { useState } from 'react';
import { ManageStructuresTable } from './ManageStructureTable';
import {
  DeleteConfirmation,
  ExportToExcel,
  globalPageSizeOptions,
  PaginationBar
} from '../../../components';
import { structure } from '../../../entityModel';
import { useApolloErrorHandler, useAuthServices } from '../../../hooks';
import {
  ADD_STRUCTURE,
  DELETE_STRUCTURE,
  UPDATE_STRUCTURE
} from '../../../api';
import { ManageStructureEditor } from './ManagerStructureEditor';

export const ManageStructures = () => {
  const { t } = useTranslation();
  const { isSignedIn } = useAuthServices();

  const [txtSearch, setTxtSearch] = useState('');
  const [alterationData, setAlterationData] = useState<structure[] | undefined>([]);

  const onTxtSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTxtSearch(event.currentTarget.value);
  };

  const [selectedStructure, setSelectedStructure] = useState<structure | undefined>(undefined);
  const [getStructures, { loading, data, error, refetch }] = useLazyQuery<getStructureOutput>(GET_STRUCTURE);
  const [getAllAlterations] = useLazyQuery<getStructureOutput>(GET_STRUCTURE, {
    onCompleted: (data) => setAlterationData(data.structures.items)
  });

  useEffect(() => {
    getStructures();
    getAllAlterations();
  }, []);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(globalPageSizeOptions[0]);

  const [openEditor, setOpenEditor] = useState(false);
  const [savingError, setSavingError] = useState<string | undefined>(undefined);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const lblSearch = capitalizeFirstLetterEveryword(t(TXT_SEARCH_BY_NAME));

  const fetchStructures = () => {
    const variables: getLithologiesInput = {
      code: txtSearch,
      take: pageSize,
      skip: (pageNumber - 1) * pageSize
    };
    getStructures({
      variables
    });
  };

  const fetch = (resetPage?: boolean) => {
    if (!!resetPage && pageNumber !== 1) {
      setPageNumber(1);
    } else {
      fetchStructures();
    }
  };

  useEffect(() => {
    useApolloErrorHandler(isSignedIn, error);
  }, [error]);

  useEffect(() => {
    fetch();
  }, [pageNumber]);

  useEffect(() => {
    fetch(true);
  }, [pageSize, txtSearch]);

  const openEditStructureEditor = (structure: structure) => {
    setSelectedStructure(structure);
    setOpenEditor(true);
  };

  const openDeleteStructureConfirmation = (structure: structure) => {
    setSelectedStructure(structure);
    setOpenDeleteConfirmation(true);
  };

  const openAddStructureEditor = () => {
    setSelectedStructure(undefined);
    setOpenEditor(true);
  };

  const closeEditor = () => {
    setOpenEditor(false);
  };

  const afterAddingOrUpdatingStructure = () => {
    setOpenEditor(false);
    if (refetch) {
      refetch();
    }
  };

  const handleAddingOrUpdatingStructureError = (error: ApolloError) => {
    if (error.graphQLErrors.length > 0 &&
      !!error.graphQLErrors[0].extensions) {
      setSavingError(error.graphQLErrors[0].extensions['message']);
    } else {
      setSavingError(error.message);
    }
  };

  const [addStructure, addStructureOperation] = useMutation<structure>(ADD_STRUCTURE, {
    onError: (e) => handleAddingOrUpdatingStructureError(e),
    onCompleted: (result) => afterAddingOrUpdatingStructure()
  });

  const [updateStructure, updateStructureOperation] = useMutation<structure>(UPDATE_STRUCTURE, {
    onError: (e) => handleAddingOrUpdatingStructureError(e),
    onCompleted: (result) => afterAddingOrUpdatingStructure()
  });

  const onEditorSave = (payload: addStructureInput | updateStructureInput) => {
    if (!selectedStructure) {
      addStructure({
        variables: {
          input: payload
        }
      });
    } else {
      updateStructure({
        variables: {
          input: payload
        }
      });
    }
  };

  const onDeleteCancel = () => setOpenDeleteConfirmation(false);

  const [deleteStructure, deleteStructureOperation] = useMutation<boolean>(DELETE_STRUCTURE, {
    onError: (e) => { },
    onCompleted: (result) => {
      if (data && refetch) {
        if (data.structures.items.length == 1) {
          const targetPage = pageNumber - 1 <= 0 ? 1 : pageNumber - 1;
          if (pageNumber !== targetPage) {
            setPageNumber(targetPage);
          } else {
            refetch();
          }
        } else {
          refetch();
        }
      }
      onDeleteCancel();
    }
  });

  const onDelete = () => {
    const input: deleteStructureInput = {
      id: selectedStructure!.id!
    };
    deleteStructure({
      variables: {
        input
      }
    });
  };

  const onPageSizeChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(+event.target.value);
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>{t(capitalizeFirstLetterEveryword(TXT_STRUCTURES))}</h1>
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          {
            openEditor &&
            <ManageStructureEditor
              structure={selectedStructure}
              onClose={closeEditor}
              onSave={onEditorSave}
              isSaving={
                !selectedStructure ? addStructureOperation.loading :
                  updateStructureOperation.loading
              }
              savingError={savingError}
            />
          }
          {
            openDeleteConfirmation &&
            <DeleteConfirmation
              loading={deleteStructureOperation.loading}
              onYes={onDelete}
              onNo={onDeleteCancel}
              objectName={selectedStructure?.label || ''}
            />
          }
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className='row'>
                    <div className='col-6'>
                      <div className="form-group">
                        <label>{lblSearch}</label>
                        <input type='text' className='form-control' value={txtSearch} onChange={onTxtSearchChanged} />
                      </div>
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-md-6'>
                      <button
                        className='btn btn-primary'
                        data-target="#modal-structure-editor"
                        data-toggle="modal"
                        disabled={loading}
                        onClick={openAddStructureEditor}>
                        {t(TXT_ADD_STRUCTURE).toUpperCase()}
                      </button>
                    </div>
                    <div className="col-md-6" style={{ textAlign: 'right' }}>
                      <ExportToExcel filename={TXT_STRUCTURES} data={alterationData} />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <ManageStructuresTable
                    loading={loading}
                    data={data}
                    error={error}
                    onEdit={openEditStructureEditor}
                    onDelete={openDeleteStructureConfirmation}
                  />
                </div>
                <PaginationBar
                  currentPage={pageNumber}
                  totalCount={data?.structures.totalCount}
                  pageSize={pageSize}
                  onPageChanged={(page) => setPageNumber(page)}
                  onChangeRowsPerPage={onPageSizeChanged}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
